/**
 * "Mark someone out" — the office's way onto the Grid for a planned absence
 * that isn't PTO (#1268): a licensing class, a school week, jury duty.
 *
 * WHY THIS ISN'T THE PTO DIALOG. Approving PTO writes payroll — PTO hours or
 * points — and consumes balance. A week at a 40-hour BPAT class is neither
 * vacation nor a call-in, and filing it as PTO says something false about the
 * employee's pay and their leave. This writes the scheduler block and nothing
 * else.
 *
 * It reads the same window back so the office can SEE what a person already
 * has on those days before adding to it, and clear the blocks it owns.
 * PTO / call-in / NCNS rows show here too, marked as belonging to their own
 * lifecycle — the server refuses to delete them and the button says so rather
 * than offering an action that would fail.
 */
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CalendarOff, CalendarX2, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import { api, ApiError } from "@/lib/api";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { describeRePaired, type OutDayRow, type RePaired } from "./out-days";
import { bookedJobsLine, type BookedJobOnOutDay } from "./out-day-booked";

export interface OutDayPerson {
    id: string;
    name: string;
    isHelper: boolean;
}

interface OutDayDialogProps {
    open: boolean;
    onClose: () => void;
    /** Everyone who can be marked out — leads and helpers alike. */
    people: OutDayPerson[];
    /** Pre-selected when opened from a lane header. */
    defaultUserId?: string | null;
    /** The day the board is showing; seeds both ends of the range. */
    defaultDate: string;
}

const apiMessage = (e: unknown) => (e instanceof ApiError ? e.message : "Something went wrong.");

export function OutDayDialog({ open, onClose, people, defaultUserId, defaultDate }: OutDayDialogProps) {
    const qc = useQueryClient();
    const [userId, setUserId] = useState(defaultUserId ?? "");
    const [startDate, setStartDate] = useState(defaultDate);
    const [endDate, setEndDate] = useState(defaultDate);
    const [reason, setReason] = useState<"training" | "other">("training");
    const [label, setLabel] = useState("");
    const [includeWeekends, setIncludeWeekends] = useState(false);
    // Lifecycle audit 10/29: jobs already booked for them on the days just
    // marked out, so they can be moved instead of sitting on an out lane.
    const [booked, setBooked] = useState<BookedJobOnOutDay[]>([]);

    // Re-seed each time it opens: the dispatcher clicked a specific lane on a
    // specific day, and a stale person from the last open is a wrong entry.
    useEffect(() => {
        if (!open) return;
        setUserId(defaultUserId ?? "");
        setStartDate(defaultDate);
        setEndDate(defaultDate);
        setReason("training");
        setLabel("");
        setIncludeWeekends(false);
        setBooked([]);
    }, [open, defaultUserId, defaultDate]);

    const rangeValid = Boolean(startDate) && Boolean(endDate) && endDate >= startDate;

    const existingQuery = useQuery({
        queryKey: ["dispatch", "out-days", "range", startDate, endDate],
        queryFn: () =>
            api.get<{ days: OutDayRow[] }>(
                `/api/dispatch/out-days?start=${encodeURIComponent(startDate)}&end=${encodeURIComponent(endDate)}`,
            ),
        enabled: open && rangeValid,
    });
    const existing = useMemo(
        () => (existingQuery.data?.days ?? []).filter((d) => d.userId === userId),
        [existingQuery.data, userId],
    );

    const invalidate = () => {
        qc.invalidateQueries({ queryKey: ["dispatch", "out-days"] });
        qc.invalidateQueries({ queryKey: ["dispatch", "pairings"] });
    };

    const save = useMutation({
        mutationFn: () =>
            api.post<{
                created: string[];
                alreadyBlocked: Array<{ date: string; label: string }>;
                bookedJobs?: BookedJobOnOutDay[];
            }>("/api/dispatch/out-days", {
                userId,
                startDate,
                endDate,
                reason,
                label: label.trim() || undefined,
                includeWeekends,
            }),
        onSuccess: (r) => {
            // Never a bare "Saved" — a range that partly collided has to say
            // which days it did NOT take, or the board quietly disagrees with
            // what the dispatcher thinks they entered.
            const n = r.created.length;
            const skipped = r.alreadyBlocked ?? [];
            if (n === 0 && skipped.length > 0) {
                toast.warning(
                    `Nothing added — ${skipped.length === 1 ? "that day was" : "those days were"} already blocked (${skipped
                        .map((s) => `${s.date}: ${s.label}`)
                        .join(", ")}).`,
                );
            } else if (skipped.length > 0) {
                toast.success(
                    `Marked out ${n} day${n === 1 ? "" : "s"}. Left alone: ${skipped
                        .map((s) => `${s.date} (${s.label})`)
                        .join(", ")}.`,
                );
            } else {
                toast.success(`Marked out ${n} day${n === 1 ? "" : "s"}.`);
            }
            setBooked(r.bookedJobs ?? []);
            if ((r.bookedJobs ?? []).length > 0) toast.warning(bookedJobsLine(r.bookedJobs ?? []));
            invalidate();
            existingQuery.refetch();
        },
        onError: (e) => toast.error(`Could not save: ${apiMessage(e)}`),
    });

    const clear = useMutation({
        mutationFn: (day: string) =>
            api.delete<{ rePaired?: RePaired[] }>(
                `/api/dispatch/out-days?userId=${encodeURIComponent(userId)}&startDate=${encodeURIComponent(
                    day,
                )}&endDate=${encodeURIComponent(day)}`,
            ),
        onSuccess: (r: { rePaired?: RePaired[] }) => {
            const first = (people.find((p) => p.id === userId)?.name ?? "They").split(" ")[0];
            const rePaired = describeRePaired(first, r?.rePaired);
            toast.success(rePaired ? `Day cleared. ${rePaired}` : "Day cleared.");
            invalidate();
            existingQuery.refetch();
        },
        onError: (e) => toast.error(`Could not clear: ${apiMessage(e)}`),
    });

    // "Move it": back to the to-schedule list, off their lane. The office
    // places it again from there (the Grid's normal drag).
    const unschedule = useMutation({
        mutationFn: (jobIds: string[]) =>
            Promise.all(
                jobIds.map((id) =>
                    api.patch(`/api/jobs/${id}`, {
                        assignedTo: null,
                        scheduledStart: null,
                        scheduledEnd: null,
                        status: "awaiting_scheduling",
                    }),
                ),
            ),
        onSuccess: (_r, jobIds) => {
            setBooked((prev) => prev.filter((j) => !jobIds.includes(j.jobId)));
            toast.success(
                jobIds.length === 1
                    ? "Moved back to the to-schedule list."
                    : `${jobIds.length} jobs moved back to the to-schedule list.`,
            );
            qc.invalidateQueries({ queryKey: ["dispatch"] });
            qc.invalidateQueries({ queryKey: ["jobs"] });
        },
        onError: (e) => toast.error(`Could not move it: ${apiMessage(e)}`),
    });
    const leadJobIds = booked.filter((j) => j.role === "lead").map((j) => j.jobId);

    const canSave = Boolean(userId) && rangeValid && !save.isPending;
    const personName = people.find((p) => p.id === userId)?.name ?? "this person";

    return (
        <Dialog open={open} onClose={onClose} title="Mark someone out">
            <div className="space-y-4 px-5 py-4">
                <p className="text-xs text-foreground/70">
                    Takes them off the auto-scheduler for these days and shows it on the board. No payroll line and no
                    attendance record — for vacation, use a PTO request instead.
                </p>

                <div className="space-y-1">
                    <label className="text-[11px] font-medium text-foreground/70" htmlFor="out-day-person">
                        Who
                    </label>
                    <Select id="out-day-person" value={userId} onChange={(e) => setUserId(e.target.value)}>
                        <option value="">Choose a person…</option>
                        {people.map((p) => (
                            <option key={p.id} value={p.id}>
                                {p.name}
                                {p.isHelper ? " (helper)" : ""}
                            </option>
                        ))}
                    </Select>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-1">
                        <label className="text-[11px] font-medium text-foreground/70" htmlFor="out-day-start">
                            First day
                        </label>
                        <Input
                            id="out-day-start"
                            type="date"
                            value={startDate}
                            onChange={(e) => {
                                setStartDate(e.target.value);
                                if (endDate < e.target.value) setEndDate(e.target.value);
                            }}
                        />
                    </div>
                    <div className="space-y-1">
                        <label className="text-[11px] font-medium text-foreground/70" htmlFor="out-day-end">
                            Last day
                        </label>
                        <Input
                            id="out-day-end"
                            type="date"
                            min={startDate}
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                        />
                    </div>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-1">
                        <label className="text-[11px] font-medium text-foreground/70" htmlFor="out-day-reason">
                            Reason
                        </label>
                        <Select
                            id="out-day-reason"
                            value={reason}
                            onChange={(e) => setReason(e.target.value as "training" | "other")}
                        >
                            <option value="training">Training / class</option>
                            <option value="other">Other</option>
                        </Select>
                    </div>
                    <div className="space-y-1">
                        <label className="text-[11px] font-medium text-foreground/70" htmlFor="out-day-label">
                            What the board should say
                        </label>
                        <Input
                            id="out-day-label"
                            maxLength={60}
                            value={label}
                            onChange={(e) => setLabel(e.target.value)}
                            placeholder={reason === "training" ? "BPAT class" : "Jury duty"}
                        />
                    </div>
                </div>

                <label className="flex items-center gap-2 text-xs text-foreground/80">
                    <Checkbox
                        checked={includeWeekends}
                        onCheckedChange={(v) => setIncludeWeekends(v === true)}
                        aria-label="Include Saturday and Sunday"
                    />
                    Include Saturday and Sunday
                </label>

                {rangeValid && userId && (
                    <div className="rounded-lg border bg-muted/40 p-3">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-foreground/70">
                            Already on {personName}&rsquo;s calendar in this range
                        </div>
                        {existingQuery.isLoading ? (
                            <p className="mt-1 text-xs text-foreground/60">Checking…</p>
                        ) : existing.length === 0 ? (
                            <p className="mt-1 text-xs text-foreground/60">Nothing — these days are clear.</p>
                        ) : (
                            <ul className="mt-1.5 space-y-1">
                                {existing.map((d) => (
                                    <li key={d.id} className="flex items-center justify-between gap-2 text-xs">
                                        <span>
                                            <span className="font-medium">{d.date}</span> — {d.label}
                                        </span>
                                        {d.clearable ? (
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                disabled={clear.isPending}
                                                onClick={() => clear.mutate(d.date)}
                                            >
                                                <Trash2 className="h-3.5 w-3.5" /> Clear
                                            </Button>
                                        ) : (
                                            <span
                                                className="shrink-0 text-[10px] italic text-muted-foreground"
                                                title="This day belongs to a PTO request or a call-in. Cancel that record to free the day."
                                            >
                                                from {d.reason === "pto" ? "a PTO request" : "a call-in"}
                                            </span>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}

                {booked.length > 0 && (
                    <div
                        className="rounded-lg border border-amber-400/60 bg-amber-50 p-3 dark:bg-amber-950/30"
                        data-testid="out-day-booked-jobs"
                    >
                        <div className="flex items-center gap-2 text-xs font-semibold">
                            <CalendarX2 className="h-4 w-4 text-amber-700" />
                            {bookedJobsLine(booked)}
                        </div>
                        <ul className="mt-2 space-y-1.5">
                            {booked.map((j) => (
                                <li key={j.jobId} className="flex items-center justify-between gap-2 text-xs">
                                    <span className="min-w-0">
                                        <span className="font-medium">{j.day}</span> —{" "}
                                        <Link to={`/jobs/${j.jobId}`} className="text-primary hover:underline">
                                            {j.siteName ?? j.customerName ?? j.title ?? "Job"}
                                            {j.jobNumber ? ` #${j.jobNumber}` : ""}
                                        </Link>
                                        {j.role === "helper" && (
                                            <span className="text-muted-foreground"> (on the crew)</span>
                                        )}
                                    </span>
                                    {j.role === "lead" ? (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            disabled={unschedule.isPending}
                                            onClick={() => unschedule.mutate([j.jobId])}
                                        >
                                            Move it
                                        </Button>
                                    ) : (
                                        <span className="shrink-0 text-[10px] italic text-muted-foreground">
                                            take them off the crew on the job
                                        </span>
                                    )}
                                </li>
                            ))}
                        </ul>
                        {leadJobIds.length > 1 && (
                            <Button
                                size="sm"
                                className="mt-2"
                                disabled={unschedule.isPending}
                                onClick={() => unschedule.mutate(leadJobIds)}
                            >
                                Move all {leadJobIds.length} back to the to-schedule list
                            </Button>
                        )}
                    </div>
                )}

                <div className="flex justify-end gap-2 border-t pt-3">
                    <Button variant="ghost" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button disabled={!canSave} onClick={() => save.mutate()}>
                        <CalendarOff className="h-4 w-4" />
                        {save.isPending ? "Saving…" : "Mark out"}
                    </Button>
                </div>
            </div>
        </Dialog>
    );
}
